import {
  BannerConfig,
  DateTools,
  SolBanner,
  SolBannerAction,
  SolBannerMood,
  SolColor,
  SolTag,
  SolTagColor,
  SolText,
} from '@sol/components';
import { SolIcon } from '@sol/icons';
import {
  PlatformBannerBoundary,
  PlatformBannerContent,
  PlatformBannerDisplayItem,
  PlatformBannerLevel,
  PlatformBannerStatus,
  PlatformBannerStatusAction,
} from '../models/platform-banner';

/**
 * The rules more than one banner surface needs: the level ordering and colours,
 * the status labels and the contract's status rank, the table of which hand
 * actions each status allows, and the single definition of how a banner renders.
 * Every surface reads these; none decides for itself, and every status and level
 * literal in the feature lives here.
 */
export class AsmBannerTools {
  public static readonly LEVEL_ORDER: readonly PlatformBannerLevel[] = ['critical', 'warning', 'info'];

  public static readonly MAX_MESSAGE_LENGTH = 230;

  /** The format a banner's own timestamps read in — created, and anything else that is a day. */
  public static readonly DISPLAY_DATE_FORMAT = 'd MMM yyyy';

  private static readonly DISPLAY_INSTANT_FORMAT = 'd MMM yyyy HH:mm';

  private static readonly LEVEL_LABELS: Record<PlatformBannerLevel, string> = {
    info: 'asm-banners.level.info',
    warning: 'asm-banners.level.warning',
    critical: 'asm-banners.level.critical',
  };

  private static readonly LEVEL_COLORS: Record<PlatformBannerLevel, SolColor> = {
    info: 'blue',
    warning: 'warning',
    critical: 'danger',
  };

  private static readonly STATUS_LABELS: Record<PlatformBannerStatus, string> = {
    draft: 'asm-banners.status.draft',
    scheduled: 'asm-banners.status.scheduled',
    live: 'asm-banners.status.live',
    ended: 'asm-banners.status.ended',
    stopped: 'asm-banners.status.stopped',
  };

  private static readonly STATUS_TAGS: Record<PlatformBannerStatus, SolTagColor> = {
    draft: 'grey',
    scheduled: 'blue',
    live: 'success',
    ended: 'grey',
    stopped: 'grey',
  };

  private static readonly ACTION_LABELS: Record<PlatformBannerStatusAction, string> = {
    enable: 'asm-banners.action.enable',
    disable: 'asm-banners.action.disable',
  };

  private static readonly DISABLED_ACTIONS: readonly PlatformBannerStatusAction[] = ['enable'];

  private static readonly ENABLED_ACTIONS: Record<PlatformBannerStatus, readonly PlatformBannerStatusAction[]> = {
    draft: [],
    scheduled: [],
    live: ['disable'],
    ended: ['enable'],
    stopped: [],
  };

  private constructor() {}

  public static levelLabel(level: PlatformBannerLevel): SolText {
    return SolText.of(AsmBannerTools.LEVEL_LABELS[level]);
  }

  public static levelTag(level: PlatformBannerLevel): SolTag {
    return SolTag.of(AsmBannerTools.levelLabel(level))
      .withTextColor('ink')
      .withTextStyle('bold')
      .withIcon(SolIcon.circle_filled_small)
      .withIconColor(AsmBannerTools.LEVEL_COLORS[level]);
  }

  public static statusLabel(status: PlatformBannerStatus): SolText {
    return SolText.of(AsmBannerTools.STATUS_LABELS[status]);
  }

  public static statusTag(status: PlatformBannerStatus): SolTag {
    return SolTag.of(AsmBannerTools.statusLabel(status)).withColor(AsmBannerTools.STATUS_TAGS[status]);
  }

  /** True while a banner is enabled and waiting for a start instant still ahead of it. */
  public static isScheduled(status: PlatformBannerStatus): boolean {
    return status === 'scheduled';
  }

  /** True for a banner that has never run and holds no schedule to run on. */
  public static isDraft(status: PlatformBannerStatus): boolean {
    return status === 'draft';
  }

  /** The translation key a menu item carries for a hand action. */
  public static actionName(action: PlatformBannerStatusAction): string {
    return AsmBannerTools.ACTION_LABELS[action];
  }

  /** The hand actions a banner offers, read from both facts a row carries: `enabled` and the status. */
  public static allowedActions(status: PlatformBannerStatus, enabled: boolean): readonly PlatformBannerStatusAction[] {
    return enabled ? AsmBannerTools.ENABLED_ACTIONS[status] : AsmBannerTools.DISABLED_ACTIONS;
  }

  /** True once an instant is behind the moment given; the contract's end is inclusive, so equal still runs. */
  public static isPast(instant: PlatformBannerBoundary, now: Date): boolean {
    return new Date(instant).getTime() < now.getTime();
  }

  /**
   * True when a write would leave the banner enabled while the end instant **that write leaves in
   * force** has passed — the one write the server answers by clearing both dates and opening a
   * fresh run.
   *
   * Both arguments are the effective values, not the stored ones: a body that omits a field leaves
   * the stored value standing, so a caller whose write omits one passes what is stored.
   */
  public static revivesFinishedBanner(
    endsAt: PlatformBannerBoundary | undefined,
    enabled: boolean,
    now: Date,
  ): boolean {
    return enabled && endsAt !== undefined && AsmBannerTools.isPast(endsAt, now);
  }

  /**
   * A schedule boundary or an interval edge as the **reader's own zone** writes it, with the
   * zone named. Every surface reads a banner's moments through this, so the table and the side
   * panel cannot disagree, and neither shows UTC: UTC is what the wire speaks, and a reader
   * should not do arithmetic to learn when a maintenance window starts.
   *
   * The zone travels with the moment rather than sitting once in a column header, for two
   * reasons. A column name is a bare translation key and carries no runtime parameter, so a
   * zone there costs the header its translation; and an interval that spans a daylight-saving
   * change has a **different** offset at each end, which one shared token would misname.
   */
  public static readerMoment(value: PlatformBannerBoundary): string {
    const at = new Date(value);
    const moment = DateTools.format(at, AsmBannerTools.DISPLAY_INSTANT_FORMAT) ?? '';
    return `${moment} ${AsmBannerTools.readerZone(at)}`;
  }

  /**
   * The reader's zone as a short offset — `GMT+2`, `GMT-7`, `GMT+5:30`, or `GMT` where there is no
   * offset to name — read **for the instant given** rather than for now, so a summer boundary and a
   * winter one each name the offset in force at it.
   *
   * It is worked out from the offset itself rather than asked of `Intl`, which spells the same zone
   * differently on different engines: a browser two versions apart answers `GMT` where another
   * answers `GMT+0`, and two readers in one zone would see one boundary labelled two ways.
   */
  public static readerZone(at: Date): string {
    const minutes = -at.getTimezoneOffset();
    if (minutes === 0) {
      return 'GMT';
    }
    const rest = Math.abs(minutes) % 60;
    const hours = `${minutes < 0 ? '-' : '+'}${Math.floor(Math.abs(minutes) / 60)}`;
    return `GMT${hours}${rest === 0 ? '' : `:${String(rest).padStart(2, '0')}`}`;
  }

  /**
   * The zone an author is picking in, as the place the browser says it is in — `Europe/Brussels`. The
   * dialog says this once, because an author choosing a moment is choosing it in a zone they never
   * stated. It names the **place** and not an offset: one dialog can pick either side of a
   * daylight-saving change, so an offset resolved now would misname half the moments it covers, and
   * every rendered moment carries its own offset already. Only a browser that will not name a place
   * falls back to one.
   */
  public static pickingZoneName(at: Date): string {
    return AsmBannerTools.resolvedZone() ?? AsmBannerTools.readerZone(at);
  }

  /**
   * The address a banner's link opens, or undefined when it names nothing openable. An
   * author may type a bare host, which the contract accepts, so a destination without a
   * scheme is read as ordinary web address rather than resolved against the platform's
   * own URL; any other scheme is refused.
   */
  public static linkHref(linkUrl: string | undefined): string | undefined {
    const trimmed = (linkUrl ?? '').trim();
    if (trimmed === '') {
      return undefined;
    }
    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }
    return /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? undefined : `https://${trimmed}`;
  }

  /**
   * The single definition of how a banner renders, which the strip and every preview render
   * through. The link is offered only when there is an address to open it with, so the action
   * and what the action does are decided by one rule rather than two.
   */
  public static toSolBanner(content: PlatformBannerContent): SolBanner {
    return SolBanner.of()
      .withMood(AsmBannerTools.toMood(content.level))
      .withDescription(SolText.ofRaw(content.message))
      .withDismissible(false)
      .withAction(AsmBannerTools.linkAction(content))
      .withRendered(true);
  }

  /** The shared surface's configs, keyed by banner id, each projected from {@link toSolBanner}. */
  public static toStripConfigs(items: readonly PlatformBannerDisplayItem[]): BannerConfig[] {
    return items.map(item => AsmBannerTools.toStripConfig(item));
  }

  private static toStripConfig(item: PlatformBannerDisplayItem): BannerConfig {
    const banner = AsmBannerTools.toSolBanner(item);
    return {
      key: String(item.id),
      mood: banner.mood,
      description: banner.description,
      action: banner.action,
    };
  }

  private static linkAction(content: PlatformBannerContent): SolBannerAction | undefined {
    if (content.linkText === undefined || AsmBannerTools.linkHref(content.linkUrl) === undefined) {
      return undefined;
    }
    return { text: SolText.ofRaw(content.linkText), appendIcon: SolIcon.open_new };
  }

  private static toMood(level: PlatformBannerLevel): SolBannerMood {
    return level === 'critical' ? 'danger' : level;
  }

  private static resolvedZone(): string | undefined {
    try {
      const zone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
      return zone === undefined || zone === '' ? undefined : zone;
    } catch {
      return undefined;
    }
  }
}
